import { useState } from 'react';

import { TextInput } from '@/components/ui/form';
import {
  formatExpiryDate,
  formatExpiryInput,
  parseExpiryInput,
  validateExpiry,
} from '@/lib/billing/payment-method-expiry-validation';

interface ExpiryInputProps {
  label?: string;
  helperText?: string;
  initialMonth?: number | null;
  initialYear?: number | null;
  disabled?: boolean;
  onChange?: (expMonth: number, expYear: number) => void;
  serverErrors?: string;
}

/**
 * Credit card expiry date input component
 * Handles formatting, validation, and user input
 */
export function ExpiryInput({
  label,
  helperText,
  initialMonth,
  initialYear,
  disabled = false,
  onChange,
  serverErrors,
}: ExpiryInputProps) {
  const [error, setError] = useState<string | null>(null);
  const [value, setValue] = useState<string>(() =>
    formatExpiryDate(initialMonth || null, initialYear || null),
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const isDeleting = inputValue.length < value.length;
    const formatted = formatExpiryInput(inputValue, isDeleting);
    setValue(formatted);

    const { month, year } = parseExpiryInput(inputValue);

    // Validate when expiry date is changed.
    const validationResult = validateExpiry(month, year);
    if (!validationResult.isValid) {
      setError(validationResult.error ?? 'Invalid expiration date');
    } else {
      setError(null);
    }

    // Always notify parent so form data reflects actual input (including invalid values)
    // The parent's handleSubmit will re-validate before submitting
    if (onChange) {
      onChange(month, year);
    }
  };

  return (
    <TextInput
      id="expiry"
      type="text"
      label={label}
      helperText={helperText}
      placeholder="MM/YYYY"
      value={value}
      onChange={handleChange}
      maxLength={7}
      disabled={disabled}
      size="lg"
      errorText={error || serverErrors}
    />
  );
}
